import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Building2, 
  Camera, 
  AlertTriangle, 
  KeyRound, 
  FileText, 
  Calendar,
  CalendarClock,
  Zap,
  Settings,
  Calculator,
  Brain,
  Flame,
  Shield,
  Route,
  Users,
  UserCog,
  Scale,
  Sparkles,
  Smartphone,
  LogOut,
  User,
  Wrench,
  X
} from 'lucide-react';

const Sidebar = ({ onClose, isMobile }) => {
  const { user, logout } = useAuth();
  
  const userRole = user?.role || 'user';
  const isAgent = userRole === 'user' || userRole === 'agent';
  const isAdmin = userRole === 'admin' || userRole === 'manager';
  
  const handleNavClick = () => {
    if (isMobile && onClose) onClose();
  };

  const agentMenuItems = [
    { icon: LayoutDashboard, label: 'Mon Espace', path: '/dashboard' },
    { icon: Sparkles, label: 'ALFA Federe', path: '/dashboard/alfa-federated', badge: 'IA' },
    { icon: Brain, label: 'SecurIA', path: '/dashboard/securia', badge: 'IA' },
    { icon: Scale, label: 'Tribunal IA', path: '/dashboard/tribunal-ia', badge: '3xLLM' },
    { icon: Brain, label: 'IA Insights', path: '/dashboard/ai-insights' },
    { icon: FileText, label: 'Main Courante', path: '/dashboard/main-courante' },
    { icon: Route, label: 'Rondes', path: '/dashboard/rondes' },
  ];

  const agentSSIItems = [
    { icon: Flame, label: 'SSI Incendie', path: '/dashboard/ssi' },
    { icon: Wrench, label: 'Equipements', path: '/dashboard/ssi/equipements' },
  ];

  const adminMenuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/dashboard' },
    { icon: Sparkles, label: 'ALFA Federe', path: '/dashboard/alfa-federated', badge: 'IA' },
    { icon: Brain, label: 'SecurIA', path: '/dashboard/securia', badge: 'IA' },
    { icon: Scale, label: 'Tribunal IA', path: '/dashboard/tribunal-ia', badge: '3xLLM' },
    { icon: Brain, label: 'IA Insights', path: '/dashboard/ai-insights' },
    { icon: FileText, label: 'Main Courante', path: '/dashboard/main-courante' },
    { icon: Route, label: 'Rondes', path: '/dashboard/rondes' },
    { icon: Shield, label: 'Controles CNAPS', path: '/dashboard/registre-controles', badge: 'LEGAL' },
    { icon: FileText, label: 'Registre Personnel', path: '/dashboard/registre-personnel', badge: 'RH' },
    { icon: Building2, label: 'Sites & Clients', path: '/dashboard/sites' },
    { icon: Users, label: 'Agents', path: '/dashboard/agents' },
    { icon: Camera, label: 'Cameras', path: '/dashboard/cameras' },
    { icon: AlertTriangle, label: 'Alarmes', path: '/dashboard/alarms' },
    { icon: KeyRound, label: 'Controle d\'acces', path: '/dashboard/access' },
    { icon: FileText, label: 'Rapports Securite', path: '/dashboard/security-reports' },
    { icon: Calendar, label: 'Planning Agents', path: '/dashboard/planning' },
    { icon: CalendarClock, label: 'Planning Avance', path: '/dashboard/planning-advanced', badge: 'PRO' },
    { icon: Zap, label: 'Planning PRO', path: '/dashboard/planning-pro', badge: 'V9' },
    { icon: Calculator, label: 'Calcul Paie', path: '/dashboard/salary-calculator' },
    { icon: Brain, label: 'Centre Paie & IA', path: '/dashboard/centre-paie-ia', badge: 'PRO' },
    { icon: Calculator, label: 'Variables de paie', path: '/dashboard/variables-paie', badge: 'RH' },
    { icon: Users, label: 'Dashboard RH', path: '/dashboard/dashboard-rh', badge: 'NEW' },
    { icon: Shield, label: 'Securite Plateforme', path: '/dashboard/cybersec', badge: 'WAF' },
  ];

  const gestionItems = [
    { icon: FileText, label: 'Abonnements', path: '/dashboard/abonnements' },
    { icon: FileText, label: 'DPAE & CNAPS', path: '/dashboard/declarations' },
    { icon: FileText, label: 'Documents IA', path: '/dashboard/documents' },
  ];

  const ssiItems = [
    { icon: Flame, label: 'SSI Incendie', path: '/dashboard/ssi' },
    { icon: Wrench, label: 'Equipements', path: '/dashboard/ssi/equipements' },
  ];

  const menuItems = isAgent ? agentMenuItems : adminMenuItems;
  const ssiMenuItems = isAgent ? agentSSIItems : ssiItems;

  const getRoleBadge = (role) => {
    const badges = {
      'admin': { label: 'Admin', color: 'bg-cyan-500' },
      'manager': { label: 'Manager', color: 'bg-violet-500' },
      'supervisor': { label: 'Superviseur', color: 'bg-blue-500' },
      'secretary': { label: 'Secretaire', color: 'bg-emerald-500' },
      'user': { label: 'Agent', color: 'bg-slate-500' },
      'agent': { label: 'Agent', color: 'bg-slate-500' }
    };
    return badges[role] || badges['user'];
  };

  const roleBadge = getRoleBadge(userRole);

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      end={item.path === '/dashboard'}
      onClick={handleNavClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 group ${
          isActive
            ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-600/25'
            : 'text-slate-300 hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <item.icon className="w-4 h-4 flex-shrink-0" />
      <span className="truncate font-medium">{item.label}</span>
      {item.badge && (
        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-white/15 text-white rounded flex-shrink-0">
          {item.badge}
        </span>
      )}
    </NavLink>
  );

  return (
    <div className="w-64 bg-slate-900 text-white flex flex-col h-full" data-testid="sidebar">
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_0f1068d2-e428-4abf-9ea6-c10e13458ae4/artifacts/khbka7ja_te%CC%81le%CC%81chargement%20%2845%29.jpeg" 
              alt="Logo" 
              className="w-10 h-10 object-contain rounded-lg"
            />
            <div>
              <h2 className="text-sm font-bold text-white tracking-wide">SecurESSI-AI</h2>
              <p className="text-[10px] text-slate-400">v3.0 PRO</p>
            </div>
          </div>
          {isMobile && (
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" data-testid="sidebar-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* User card */}
        <div className="mt-3 p-2.5 bg-white/5 rounded-lg border border-white/5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-white">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-white truncate">{user?.name || 'Utilisateur'}</p>
              <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${roleBadge.color} text-white mt-0.5`}>
                {roleBadge.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto scrollbar-thin">
        {isAgent && (
          <div className="mb-3 p-2.5 bg-cyan-900/30 border border-cyan-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-cyan-400">
              <Smartphone className="w-3.5 h-3.5" />
              <span className="text-[10px] font-semibold tracking-wider uppercase">Mode Agent</span>
            </div>
          </div>
        )}

        {menuItems.map((item) => (
          <NavItem key={item.path} item={item} />
        ))}

        {/* SSI Section */}
        <div className="pt-3 mt-3 border-t border-white/5">
          <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
            Securite Incendie
          </p>
          {ssiMenuItems.map((item) => (
            <NavItem key={item.path} item={item} />
          ))}
        </div>

        {/* Gestion - Admin only */}
        {isAdmin && (
          <div className="pt-3 mt-3 border-t border-white/5">
            <p className="px-3 py-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              Gestion
            </p>
            {gestionItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>
        )}

        {/* Settings - Admin only */}
        {isAdmin && (
          <div className="pt-3 mt-3 border-t border-white/5">
            <NavLink
              to="/dashboard/users"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive ? 'bg-cyan-600 text-white' : 'text-slate-300 hover:bg-white/5'
                }`
              }
            >
              <UserCog className="w-4 h-4" />
              <span className="font-medium">Utilisateurs</span>
            </NavLink>
            <NavLink
              to="/dashboard/settings"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive ? 'bg-cyan-600 text-white' : 'text-slate-300 hover:bg-white/5'
                }`
              }
            >
              <Settings className="w-4 h-4" />
              <span className="font-medium">Parametres</span>
            </NavLink>
          </div>
        )}

        {/* Logout */}
        <div className="pt-3 mt-3 border-t border-white/5">
          <button
            onClick={() => { logout(); handleNavClick(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-all"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="font-medium">Deconnexion</span>
          </button>
        </div>
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-white/5">
        <div className="text-center space-y-0.5">
          <p className="text-[10px] text-slate-500">High Tech Security LTD</p>
          <p className="text-[10px] text-slate-600">Secutor Pro LLC</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
